import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from 'src/base-component';
import { Breadcrumb, Card, Col, DatePicker, Row, Select, Table } from 'antd';
import { AnalyticsApi } from 'src/api';
import moment from 'moment';
import { Column, Line, Bar } from '@ant-design/charts';
import type { RangePickerProps } from 'antd/es/date-picker';

// Імпортуємо інтерфейси для аналітики продуктів
import type {
    IProductAnalytics,
    ICategoryAnalytics,
    IManufacturerAnalytics,
    IProductMonthlyAnalytics
} from 'src/api/AnalyticsApi';

const { RangePicker } = DatePicker;

export const AnalyticsProductsPage: React.FC = () => {
    const [productAnalytics, setProductAnalytics] = useState<IProductAnalytics[]>([]);
    const [categoryAnalytics, setCategoryAnalytics] = useState<ICategoryAnalytics[]>([]);
    const [manufacturerAnalytics, setManufacturerAnalytics] = useState<IManufacturerAnalytics[]>([]);
    const [monthlyAnalytics, setMonthlyAnalytics] = useState<IProductMonthlyAnalytics[]>([]);
    const [loading, setLoading] = useState(true);
    const [dateRange, setDateRange] = useState<[moment.Moment, moment.Moment] | null>(null);
    const [selectedYear, setSelectedYear] = useState<string>(moment().format('YYYY'));

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params = dateRange ? {
                dateFrom: dateRange[0].format('YYYY-MM-DD'),
                dateTo: dateRange[1].format('YYYY-MM-DD')
            } : undefined;

            const [productRes, categoryRes, manufacturerRes, monthlyRes] = await Promise.all([
                AnalyticsApi.getProductAnalytics(params),
                AnalyticsApi.getCategoryAnalytics(params),
                AnalyticsApi.getManufacturerAnalytics(params),
                AnalyticsApi.getProductMonthlyAnalytics({ year: selectedYear })
            ]);

            if (productRes?.data) setProductAnalytics(productRes.data);
            if (categoryRes?.data) setCategoryAnalytics(categoryRes.data);
            if (manufacturerRes?.data) setManufacturerAnalytics(manufacturerRes.data);
            if (monthlyRes?.data) setMonthlyAnalytics(monthlyRes.data);
        } catch (error) {
            console.error('Error loading product analytics data:', error);
        } finally {
            setLoading(false);
        }
    }, [dateRange, selectedYear]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleDateRangeChange: RangePickerProps['onChange'] = (dates) => {
        setDateRange(dates ? [moment(dates[0]?.toDate()), moment(dates[1]?.toDate())] : null);
    };

    const handleYearChange = (year: string): void => {
        setSelectedYear(year);
        setDateRange([moment(`${year}-01-01`), moment(`${year}-12-31`)]);
    };

    // Колонки для таблиць
    const productColumns = [
        {
            title: 'Продукт',
            dataIndex: 'product_name',
            key: 'product_name',
        },
        {
            title: 'Артикул',
            dataIndex: 'product_model',
            key: 'product_model',
        },
        {
            title: 'Кількість',
            dataIndex: 'count',
            key: 'count',
        },
        {
            title: 'Середня ціна',
            dataIndex: 'avg_price',
            key: 'avg_price',
            render: (value: number) => value?.toLocaleString() + ' грн',
        },
        {
            title: 'Загальний дохід',
            dataIndex: 'total_revenue',
            key: 'total_revenue',
            render: (value: number) => value?.toLocaleString() + ' грн',
        },
    ];

    const categoryColumns = [
        {
            title: 'Категорія',
            dataIndex: 'category_name',
            key: 'category_name',
        },
        {
            title: 'Кількість',
            dataIndex: 'count',
            key: 'count',
        },
        {
            title: 'Загальний дохід',
            dataIndex: 'total_revenue',
            key: 'total_revenue',
            render: (value: number) => value?.toLocaleString() + ' грн',
        },
    ];

    const manufacturerColumns = [
        {
            title: 'Виробник',
            dataIndex: 'manufacturer_name',
            key: 'manufacturer_name',
        },
        {
            title: 'Кількість',
            dataIndex: 'count',
            key: 'count',
        },
        {
            title: 'Загальний дохід',
            dataIndex: 'total_revenue',
            key: 'total_revenue',
            render: (value: number) => value?.toLocaleString() + ' грн',
        },
    ];

    const monthlyColumns = [
        {
            title: 'Місяць',
            dataIndex: 'month',
            key: 'month',
        },
        {
            title: 'Кількість',
            dataIndex: 'count',
            key: 'count',
        },
        {
            title: 'Загальний дохід',
            dataIndex: 'total_revenue',
            key: 'total_revenue',
            render: (value: number) => value?.toLocaleString() + ' грн',
        },
        {
            title: 'Середня ціна',
            dataIndex: 'avg_price',
            key: 'avg_price',
            render: (value: number) => value?.toLocaleString() + ' грн',
        },
    ];

    return (
        <>
            <PageHeader
                title="Аналітика продуктів"
                subtitle="Детальна аналітика по продуктах"
                breadcrumbs={
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            <a href="/">Головна</a>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>Аналітика</Breadcrumb.Item>
                        <Breadcrumb.Item>Продукти</Breadcrumb.Item>
                    </Breadcrumb>
                }
                extra={[
                    <RangePicker
                        key="dateRange"
                        onChange={handleDateRangeChange}
                        placeholder={['Дата від', 'Дата до']}
                        style={{ marginRight: 16 }}
                    />,
                    <Select
                        key="year"
                        value={selectedYear}
                        onChange={handleYearChange}
                        style={{ width: 120 }}
                        placeholder="Рік"
                    >
                        {Array.from({ length: 5 }, (_, i) => moment().subtract(i, 'year').format('YYYY')).map(year => (
                            <Select.Option key={year} value={year}>{year}</Select.Option>
                        ))}
                    </Select>
                ]}
            />

            {/* Графіки та таблиці */}
            {/* Ряд 1: Розподіл по продуктах */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                    <Card title="Топ продуктів за кількістю" loading={loading}>
                        <Column
                            data={productAnalytics.slice(0, 10)} // Топ 10 продуктів
                            xField="product_name"
                            yField="count"
                            yAxis={{
                                label: {
                                    formatter: (v: string) => `${Number(v).toLocaleString()} шт`,
                                },
                            }}
                            meta={{
                                count: {
                                    alias: 'Кількість',
                                },
                            }}
                            tooltip={{
                                title: 'Кількість продажів',
                                formatter: (datum: IProductAnalytics) => ({
                                    name: 'Кількість',
                                    value: datum.count?.toLocaleString() + ' шт',
                                }),
                            }}
                            height={300}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Детальні дані по продуктах" loading={loading}>
                        <Table
                            dataSource={productAnalytics}
                            columns={productColumns}
                            pagination={{ pageSize: 10 }}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>

            {/* Ряд 2: Розподіл по категоріях */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                    <Card title="Розподіл по категоріях" loading={loading}>
                        <Column
                            data={categoryAnalytics}
                            xField="category_name"
                            yField="count"
                            yAxis={{
                                label: {
                                    formatter: (v: string) => `${Number(v).toLocaleString()} шт`,
                                },
                            }}
                            meta={{
                                count: {
                                    alias: 'Кількість',
                                },
                            }}
                            tooltip={{
                                title: 'Кількість продажів',
                                formatter: (datum: ICategoryAnalytics) => ({
                                    name: 'Кількість',
                                    value: datum.count?.toLocaleString() + ' шт',
                                }),
                            }}
                            height={300}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Детальні дані по категоріях" loading={loading}>
                        <Table
                            dataSource={categoryAnalytics}
                            columns={categoryColumns}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>

            {/* Ряд 3: Динаміка по місяцях */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                    <Card title="Динаміка продажів по місяцях" loading={loading}>
                        <Line
                            data={monthlyAnalytics}
                            xField="month"
                            yField="total_revenue"
                            yAxis={{
                                label: {
                                    formatter: (v: string) => `${Number(v).toLocaleString()} грн`,
                                },
                            }}
                            tooltip={{
                                title: 'Дохід',
                                formatter: (datum: IProductMonthlyAnalytics) => ({
                                    name: 'Дохід',
                                    value: datum.total_revenue?.toLocaleString() + ' грн',
                                }),
                            }}
                            point={{
                                size: 5,
                                shape: 'diamond',
                            }}
                            height={300}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Детальні дані по місяцях" loading={loading}>
                        <Table
                            dataSource={monthlyAnalytics}
                            columns={monthlyColumns}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>

            {/* Ряд 4: Ефективність виробників */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                    <Card title="Ефективність виробників" loading={loading}>
                        <Bar
                            data={manufacturerAnalytics}
                            xField="total_revenue"
                            yField="manufacturer_name"
                            xAxis={{
                                label: {
                                    formatter: (v: string) => `${Number(v).toLocaleString()} грн`,
                                },
                            }}
                            tooltip={{
                                title: 'Дохід',
                                formatter: (datum: IManufacturerAnalytics) => ({
                                    name: 'Дохід',
                                    value: datum.total_revenue?.toLocaleString() + ' грн',
                                }),
                            }}
                            height={300}
                        />
                    </Card>
                </Col>
                <Col span={12}>
                    <Card title="Детальні дані по виробниках" loading={loading}>
                        <Table
                            dataSource={manufacturerAnalytics}
                            columns={manufacturerColumns}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
            </Row>
        </>
    );
};
